import { Play, Pause, RotateCcw, X, Zap } from 'lucide-react'

const SLIDER = `w-full h-1 bg-gray-700 rounded-full appearance-none cursor-pointer
  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
  [&::-webkit-slider-thumb]:bg-green-400 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer
  [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:bg-green-400
  [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer`

interface BrainControlPanelProps {
  isRunning: boolean
  onToggleRun: () => void
  edgeTimeMs: number
  onEdgeTimeChange: (v: number) => void
  fadeMs: number
  onFadeChange: (v: number) => void
  onRestart: () => void
  onClose: () => void
  nodeCount: number
  litNodeCount: number
  fps: number
  memMB: number
}

export default function BrainControlPanel({
  isRunning,
  onToggleRun,
  edgeTimeMs,
  onEdgeTimeChange,
  fadeMs,
  onFadeChange,
  onRestart,
  onClose,
  nodeCount,
  litNodeCount,
  fps,
  memMB,
}: BrainControlPanelProps) {
  const progress = nodeCount > 0 ? Math.round((litNodeCount / nodeCount) * 100) : 0

  return (
    <div className="absolute bottom-4 left-4 bg-black/80 backdrop-blur-sm rounded-xl border border-gray-800 p-4 min-w-[240px] z-10">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-green-400" />
          <span className="text-sm font-medium text-white">Neural Activity</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-gray-600 font-mono">
            {fps}fps{memMB > 0 ? ` ${memMB}MB` : ''}
          </span>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-white transition-colors">
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={onToggleRun}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            isRunning
              ? 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30'
              : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
          }`}
        >
          {isRunning ? <Pause size={12} /> : <Play size={12} />}
          {isRunning ? 'Pause' : 'Start'}
        </button>
        <button
          onClick={onRestart}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 text-gray-400 border border-gray-700 hover:text-white hover:bg-white/10 transition-colors"
        >
          <RotateCcw size={12} />
          Restart
        </button>
      </div>

      <div className="mb-3">
        <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
          <span>{litNodeCount} / {nodeCount} active</span>
          <span>{progress}%</span>
        </div>
        <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${progress}%`, background: 'linear-gradient(to right, #39ff14, #00ff41)' }}
          />
        </div>
      </div>

      <div className="mb-2.5">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>Fast</span>
          <span className="text-gray-400">Edge {edgeTimeMs}ms</span>
          <span>Slow</span>
        </div>
        <input
          type="range" min={10} max={500} step={10}
          value={edgeTimeMs}
          onChange={e => onEdgeTimeChange(parseInt(e.target.value))}
          className={SLIDER}
        />
      </div>

      <div>
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>Short</span>
          <span className="text-gray-400">Fade {fadeMs}ms</span>
          <span>Long</span>
        </div>
        <input
          type="range" min={200} max={3000} step={50}
          value={fadeMs}
          onChange={e => onFadeChange(parseInt(e.target.value))}
          className={SLIDER}
        />
      </div>
    </div>
  )
}
